import React, { useMemo, useState } from 'react';

import type Konva from 'konva';
import { merge } from 'lodash';
import { Layer, Group, Rect } from 'react-konva';

import type { TStageSize } from '@vlabs/monokit/shared/ui/canvas/adaptive-stage';
import { AdaptiveStage } from '@vlabs/monokit/shared/ui/canvas/adaptive-stage';

import { DEFAULT_STYLES } from './constants';
import type { TBbox, TBboxStyle } from './types';

export interface BboxRendererProps {
  originSize?: TStageSize;
  bboxList?: TBbox[];
  onBBoxClick?: (stack: TBbox[] | null) => void;
}

type TRenderableBbox = TBbox & { $brId: string; style: TBboxStyle };

function BboxRenderer({
  originSize = undefined,
  bboxList = [],
  onBBoxClick = undefined,
}: BboxRendererProps): React.JSX.Element {
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [stageSize, setStageSize] = useState<TStageSize | undefined>(undefined);

  const renderableBboxList: TRenderableBbox[] = useMemo(() => {
    let $bboxList = bboxList.map((box, idx) => ({
      ...box,
      $brId: String(idx),
      style: merge({}, DEFAULT_STYLES, box.style),
    }));
    if (originSize && stageSize) {
      let scale = 1;
      if ((stageSize.width / stageSize.height) > (originSize.width / originSize.height)) {
        scale = stageSize.height / originSize.height;
      } else {
        scale = stageSize.width / originSize.width;
      }
      $bboxList = $bboxList.map((bbox) => ({
        ...bbox,
        x: bbox.x * scale,
        y: bbox.y * scale,
        width: bbox.width * scale,
        height: bbox.height * scale,
      }));
    }
    return $bboxList;
  }, [bboxList, originSize, stageSize]);

  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!onBBoxClick) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const hits = stage.getAllIntersections(stage.getPointerPosition());

    // top node под курсором
    const nodeTop = hits?.[0];

    if (!nodeTop) {
      if (hoverId !== null) setHoverId(null);
      stage.container().style.cursor = 'default';
      return;
    }

    const { id } = nodeTop.attrs;
    if (id !== hoverId) setHoverId(id);
    stage.container().style.cursor = 'pointer';
  };

  const handleMouseLeave = (e: Konva.KonvaEventObject<MouseEvent>) => {
    setHoverId(null);
    const stage = e.target.getStage();
    if (!stage) return;
    stage.container().style.cursor = 'default';
  };

  const handleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!onBBoxClick) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) {
      onBBoxClick(null);
      return;
    }

    const hitNodes = stage.getAllIntersections(pos);
    const stack: TBbox[] = [];
    hitNodes.forEach((node) => {
      const nodeId = node.attrs.$brId;
      if (!nodeId) return;
      stack.push((bboxList[Number(nodeId)] as TBbox));
    });
    onBBoxClick(stack);
  };

  return (
    <AdaptiveStage
      onChangeSize={setStageSize}
      onClick={handleClick}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      originSize={originSize}
    >
      <Layer listening>
        <Group listening>
          {renderableBboxList.map((box) => {
            const isHover = !!onBBoxClick && box.$brId === hoverId;
            const state = box.isSelected ? 'selected' : (isHover ? 'hover' : 'base');

            return (
              <Rect
                {...box}
                id={box.$brId}
                key={box.$brId}
                {...box.style[state]}
              />
            );
          })}
        </Group>
      </Layer>
    </AdaptiveStage>
  );
}

export { BboxRenderer };
